import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnChanges,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
  BackgroundColor,
  CmsImage,
  FlexboxItem,
  Image,
  ImageSize,
  JustifyContent,
  OrderStyle,
  PaddingStyle,
  PTypes,
  VisibilitySettingStyle,
  WidthStyle,
} from '@digital-platform/rs-cms/domain';
import { ViewportService } from '@digital-platform/rs-shared/services';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { DynamicContentWrapperComponent } from '../dynamic-content-wrapper/dynamic-content-wrapper.component';

interface FlexboxItemAngularConfig {
  class?: string;
}

@Component({
  selector: 'digital-platform-flexbox-item',
  standalone: true,
  imports: [CommonModule, DynamicContentWrapperComponent],
  templateUrl: './flexbox-item.component.html',
  styleUrls: ['./flexbox-item.component.scss'],
})
export class FlexboxItemComponent
  extends BasePTypeComponent
  implements FlexboxItem, OnChanges
{
  public viewportService = inject(ViewportService);
  public sanitizer = inject(DomSanitizer);

  public override type = 'flexboxItem' as const;
  @Input() bgColor: BackgroundColor | null = null;
  @Input() bottomBorder = false;
  @Input() fbiDesktop: WidthStyle | null = null;
  @Input() fbiMobile: WidthStyle | null = null;
  @Input() fbiTablet: WidthStyle | null = null;
  @Input() flexboxItemDesktopOrder: OrderStyle | null = null;
  @Input() flexboxItemMobileOrder: OrderStyle | null = null;
  @Input() flexboxItemTabletOrder: OrderStyle | null = null;
  @Input() image: Image[] = [];
  @Input() imageSize: ImageSize | null = null;
  @Input() leftBorder = false;
  @Input() justifyContent: JustifyContent | null = null;
  @Input() overlayColor: string | null = null;
  @Input() mobileCenter100 = false;
  @Input() nestedFlexboxContent: PTypes[] = [];
  @Input() paddingBottom: PaddingStyle | null = null;
  @Input() paddingLeft: PaddingStyle | null = null;
  @Input() paddingRight: PaddingStyle | null = null;
  @Input() paddingTop: PaddingStyle | null = null;
  @Input() rightBorder = false;
  @Input() roundedCorner: string[] | string | null = null;
  @Input() topBorder = false;
  @Input() visibility: VisibilitySettingStyle | null = null;
  @Input() webpMedia: CmsImage[] = [];
  @Input() childStructureClasses = '';
  @Input() wrapperClasses = '';
  @Input() angularConfig?: FlexboxItemAngularConfig;
  @Input() class = '';

  public ngOnChanges(): void {
    this.class = this.stringOrNullToString(this.class);
    this.childStructureClasses = this.concatChildStructureClasses();
    this.wrapperClasses = this.concatWrapperClasses();
  }

  private stringOrNullToString(cssClass: string | null = ''): string {
    const retVal: string = cssClass || '';
    return this.sanitizer.sanitize(SecurityContext.NONE, retVal) || '';
  }

  public concatWrapperClasses(): string {
    const retVal: string[] = [];
    // Add the classes that should be applied to the host element
    retVal.push(this.class);

    if (this.angularConfig?.class) {
      retVal.push(this.angularConfig.class);
    }

    if (this.bgColor?.cssColorName) {
      retVal.push(this.bgColor?.cssColorName);
    }

    if (this.justifyContent?.justificationClass) {
      retVal.push('d-flex');
      retVal.push('wrapper-full-height');
      retVal.push(this.justifyContent.justificationClass);
    }

    if (this.roundedCorner) {
      retVal.push('rounded-border');
      if (Array.isArray(this.roundedCorner)) {
        this.roundedCorner.forEach((corner) => {
          retVal.push(`rounded-${corner}`);
        });
      } else {
        retVal.push(`rounded-${this.roundedCorner}`);
      }
    }

    return retVal.join(' ');
  }

  private concatChildStructureClasses(): string {
    const concatClasses = [
      this.fbiMobile?.widthClass,
      this.fbiTablet?.widthClass,
      this.fbiDesktop?.widthClass,
      this.flexboxItemTabletOrder?.orderClass,
      this.flexboxItemDesktopOrder?.orderClass,
      this.bgColor?.cssColorName,
      this.paddingTop?.paddingClass,
      this.paddingBottom?.paddingClass,
      this.paddingRight?.paddingClass,
      this.paddingLeft?.paddingClass,
      this.visibility?.visibilityClass,
      // this.justifyContent?.justificationClass,
    ];

    // TODO: Fix the class that CMS is exposing for mobile. It exposes order-sm-<...> but it should be order-<...> to be mobile-first
    if (this.flexboxItemMobileOrder?.orderClass) {
      concatClasses.push(
        this.flexboxItemMobileOrder.orderClass.replace(
          /order-sm-(\d)/g,
          'order-$1'
        )
      );
    }

    if (this.mobileCenter100) {
      concatClasses.push('flexbox-items-mobile-100-center');
    }

    return concatClasses.join(' ');
  }
}
